import React, { useState } from "react";
import {
  Box,
  Chip,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import {
  AddCircleOutline,
  History,
  RemoveCircleOutline,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { getMedicationRefillHistory } from "../../../../../../api/services/medication";
import DataTable from "../../../../../Table/DataTable";
import { QK } from "../../../../../../base/qk";

const PAGE_SIZE = 10;

const RefillHistoryTable = ({ medicineId }) => {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery(
    [QK.medicationrefillhistory, medicineId, page],
    () => getMedicationRefillHistory(medicineId, page),
    {
      enabled: !!medicineId,
      staleTime: 30000,
      keepPreviousData: true,
    }
  );

  const history = Array.isArray(data) ? data : data?.results || [];
  const totalCount = data?.count ?? history.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const headers = [
    { key: "created_at", label: "Date" },
    { key: "transaction_type", label: "Action" },
    { key: "quantity_changed", label: "Quantity" },
    { key: "balance_after", label: "Balance" },
    { key: "performed_by_name", label: "Performed By" },
    { key: "note", label: "Note" },
  ];

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderCell = (item, header) => {
    if (header.key === "created_at") {
      return (
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: "#334155",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {formatDate(item.created_at)}
        </Typography>
      );
    }

    if (header.key === "transaction_type") {
      const transactionType = String(item.transaction_type || "").toLowerCase();
      const isAddition =
        transactionType === "addition" ||
        transactionType === "add" ||
        transactionType === "refill";

      return (
        <Chip
          icon={
            isAddition ? (
              <AddCircleOutline sx={{ fontSize: 13 }} />
            ) : (
              <RemoveCircleOutline sx={{ fontSize: 13 }} />
            )
          }
          label={isAddition ? "Refill" : "Correction"}
          size="small"
          sx={{
            height: 21,
            fontSize: "0.6rem",
            fontWeight: 700,
            borderRadius: "4px",
            bgcolor: isAddition ? "#dcfce7" : "#fef3c7",
            color: isAddition ? "#166534" : "#92400e",
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />
      );
    }

    if (header.key === "quantity_changed") {
      const quantity = Number(item.quantity_changed || 0);
      return (
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 800,
            color: quantity >= 0 ? "#059669" : "#dc2626",
          }}
        >
          {quantity > 0 ? `+${quantity}` : quantity}
        </Typography>
      );
    }

    if (header.key === "balance_after") {
      return (
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: "#334155",
          }}
        >
          {item.balance_after ?? "-"}
        </Typography>
      );
    }

    if (header.key === "performed_by_name") {
      return (
        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "#475569",
            fontWeight: 600,
          }}
        >
          {item.performed_by_name || "N/A"}
        </Typography>
      );
    }

    if (header.key === "note") {
      return (
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: "#64748b",
            lineHeight: 1.4,
          }}
        >
          {item.note || "-"}
        </Typography>
      );
    }

    return item[header.key] || "-";
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: "#f8fafc",
        borderTop: "1px solid #e2e8f0",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ mb: 1.25 }}
      >
        <History sx={{ fontSize: 17, color: "#2469F0" }} />

        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 800,
            color: "#334155",
            letterSpacing: 0.2,
          }}
        >
          REFILL HISTORY
        </Typography>

        <Chip
          label={totalCount}
          size="small"
          sx={{
            height: 18,
            minWidth: 22,
            fontSize: "0.58rem",
            fontWeight: 700,
            bgcolor: "#dbeafe",
            color: "#1d4ed8",
          }}
        />
      </Stack>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          overflow: "hidden",
          bgcolor: "#ffffff",
        }}
      >
        <DataTable
          data={history}
          headers={headers}
          loading={isLoading}
          renderCell={renderCell}
        />
      </Box>

      {!isLoading && totalCount > 0 && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            shape="rounded"
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default RefillHistoryTable;